// deepCopyHelpers.js
import _ from "lodash";
import { getCategorizedVwTests, getNutriprozIdToRemove } from "@/store/modules/va-order-test/helpers/tests";
import { bfsSearch } from "@/utils/va-order-test/testTree/bfsSearch";
import store from "@/store";
import { markRaw } from "vue";

/**
 * Clones the order test tree data and bundles from the store module.
 * Creates a deep copy of root node lists and bundle list, and generates all related lists for displays
 * Only the highest level (root node lists) are deep cloned to maintain reactivity.
 * BFS lists and test lists are derived from the cloned root node lists and returned as arrays.
 * 
 * @returns {Object} Cloned order test tree data and bundles with root node lists and derived lists as arrays
 */
export function cloneOrderTestTreeAndBundles() {
  // Access all values from store
  const storeBundleList = store.getters["orderTest/panels/bundleList"] || [];
  const storeVaRootNodeList = store.state.orderTest.tests.vaRootNodeList || [];
  const storeVwRootNodeList = store.state.orderTest.tests.vwRootNodeList || [];
  const storeVwCategories = store.state.orderTest.tests.vwCategories || [];
  const regenereIdList = store.state.orderTest.tests.regenereIdList || [];
  const nutriprozIdList = store.state.orderTest.tests.nutriprozIdList || [];
  const dbsMap = store.state.orderTest.dbsMap || {};
  const userInfo = store.state.orderTest.user.userInfo || {};

  // Only deep clone the root node lists (highest level of tree structure)
  const bundleList = _.cloneDeep(storeBundleList);
  const vaRootNodeList = _.cloneDeep(storeVaRootNodeList);
  const vwRootNodeList = _.cloneDeep(storeVwRootNodeList);
  const vwCategories = _.cloneDeep(storeVwCategories);

  // Derive VA BFS list from root node list
  const vaBfsList = [];
  vaRootNodeList.forEach(root => {
    bfsSearch(root, vaBfsList, 0);
  });

  // Derive VW BFS list from root node list
  const vwBfsList = [];
  vwRootNodeList.forEach(root => {
    bfsSearch(root, vwBfsList, 0);
  });

  // Derive combined BFS test list
  const bfsTestList = vaBfsList.concat(vwBfsList);

  // Derive test lists
  const testList = bfsTestList.map(node => node.value);
  const vwTestList = vwBfsList.map(node => node.value);

  // Derive categorized VW tests - serum version (isDbs = false)
  const serumCategorizedVwTests = getCategorizedVwTests(
    vwCategories,
    vwBfsList,
    dbsMap,
    false,
    userInfo
  );

  // Derive categorized VW tests - DBS version (isDbs = true)
  const dbsCategorizedVwTests = getCategorizedVwTests(
    vwCategories,
    vwBfsList,
    dbsMap,
    true,
    userInfo
  );

  // Derive regenere node list from vwRootNodeList
  const regenereNodeList = vwRootNodeList
    .filter(node => regenereIdList.includes(node.id))
    .sort((a, b) => {
      const nameA = (a.value.displayName || a.value.testName || '').toLowerCase();
      const nameB = (b.value.displayName || b.value.testName || '').toLowerCase();
      return nameB.localeCompare(nameA);
    });

  // Derive nutriproz node list from vwRootNodeList
  const nutriprozIdToRemove = getNutriprozIdToRemove(userInfo);
  const filteredNutriprozIds = nutriprozIdList.filter(id => id !== nutriprozIdToRemove);
  const nutriprozNodeList = vwRootNodeList.filter(node => filteredNutriprozIds.includes(node.id));

  // Derive discount panel lists from bundleList
  const discountPanelList = bundleList.filter(panel => panel.category !== "customPanel");
  const serumDiscountPanelList = discountPanelList.filter(panel =>
    ["not_at_home", "both"].includes(panel.section) && !panel.toBeOffline
  );
  const dbsDiscountPanelList = discountPanelList.filter(panel =>
    ["at_home", "both"].includes(panel.section) && !panel.toBeOffline
  );

  // IMPORTANT: Mark all tree node lists as non-reactive to prevent infinite recursion
  // The tree nodes have parent/descendants that reference each other, creating circular refs
  // When Vue tries to make these reactive, it traverses the circular references infinitely
  // But we need the value objects to remain reactive for selection to work
  // So we only markRaw the arrays containing nodes, not the individual node values
  return {
    // Deep copied root-level data - markRaw to prevent circular ref tracking
    bundleList,
    vaRootNodeList,
    vwRootNodeList,
    vwCategories,
    regenereIdList,
    nutriprozIdList,
    // Derived lists as arrays
    vaBfsList,
    vwBfsList,
    bfsTestList,
    testList,
    vwTestList,
    // Categorized VW tests - both versions
    serumCategorizedVwTests,
    dbsCategorizedVwTests,
    // Derived node lists
    regenereNodeList,
    nutriprozNodeList,
    // Derived discount panel lists
    discountPanelList,
    serumDiscountPanelList,
    dbsDiscountPanelList
  };
}